import styled from '@emotion/styled';
import * as React from 'react';
import { MetaPillWidget } from './MetaPillWidget';
import { TagBadgeWidget } from './TagBadgeWidget';

type SyntaxValue = React.ReactNode | string | string[];

export interface SyntaxCardWidgetProps {
  availability?: React.ReactNode;
  children?: React.ReactNode;
  defaultValue?: SyntaxValue;
  introduced?: SyntaxValue;
  required?: boolean;
  type?: SyntaxValue;
}

export const SyntaxCardWidget: React.FC<SyntaxCardWidgetProps> = ({
  availability,
  children,
  defaultValue,
  introduced,
  required,
  type
}) => {
  const status = availability ?? (required ? 'Required' : 'Optional');

  return (
    <S.Root data-content-syntax-card>
      {type ? <MetaPillWidget label="Type">{renderSyntaxValue(type)}</MetaPillWidget> : null}
      {defaultValue ? <MetaPillWidget label="Default">{renderSyntaxValue(defaultValue)}</MetaPillWidget> : null}
      {introduced ? <MetaPillWidget label="Introduced">{renderSyntaxValue(introduced)}</MetaPillWidget> : null}
      <S.Status>{status}</S.Status>
      {children ? <S.Description>{children}</S.Description> : null}
    </S.Root>
  );
};

const renderSyntaxValue = (value: SyntaxValue): React.ReactNode => {
  if (Array.isArray(value)) {
    return (
      <S.ValueList>
        {value.map((item, index) => (
          <S.ValuePill key={`${item}-${index}`} tone="muted">
            {item}
          </S.ValuePill>
        ))}
      </S.ValueList>
    );
  }

  if (typeof value === 'string') {
    return <S.CodePill>{value}</S.CodePill>;
  }

  return value;
};

namespace S {
  export const Root = styled.aside`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 15px;
    padding: 8px;
    border: 1px solid ${(p) => p.theme.text.listBorder};
    border-radius: var(--content-list-radius);
    background: ${(p) => p.theme.text.tableBackground};
    overflow: hidden;
  `;

  export const CodePill = styled.code`
    display: inline-flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    border-radius: 999px;
    border: 0;
    background: transparent;
    color: inherit;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.95em;
    overflow-wrap: anywhere;
  `;

  export const ValueList = styled.span`
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
  `;

  export const ValuePill = styled(TagBadgeWidget)`
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
    overflow-wrap: anywhere;
  `;

  export const Status = styled.span`
    margin-left: auto;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 1.2;
    text-transform: uppercase;
    white-space: nowrap;
    opacity: 0.76;
  `;

  export const Description = styled.div`
    flex-basis: 100%;
    padding: 0 2px 2px;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 13px;
    line-height: 1.65;

    > *:first-child {
      margin-top: 0;
    }

    > *:last-child {
      margin-bottom: 0;
    }
  `;
}
